import React, { useEffect, useState, useCallback } from "react";
import { Table, Button, Select, Tag } from "antd";
import { ReloadOutlined, MailOutlined } from "@ant-design/icons";
import { getTransactionalEmailHistoryApi } from "../../services/api";
import StatCard from "./StatCard";

const TEMPLATES = [
  "order-placed", "order-cancelled", "order-delivered", "delivery-otp",
  "account-created", "account-updated", "change-password", "forgot-password",
  "review-request", "abandoned-cart",
];

export default function TransactionalEmailLog() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [template, setTemplate] = useState(undefined);
  const [success, setSuccess] = useState(undefined);
  const [lastUpdated, setLastUpdated] = useState(null);

  const fetchHistory = useCallback(async () => {
    setLoading(true);
    try {
      const params = {};
      if (template) params.template = template;
      if (success !== undefined) params.success = success;
      const res = await getTransactionalEmailHistoryApi(params);
      setRows(res.data?.data || []);
      setLastUpdated(new Date());
    } catch {
      // Non-fatal — table just stays on whatever it last had.
    } finally {
      setLoading(false);
    }
  }, [template, success]);

  useEffect(() => { fetchHistory(); }, [fetchHistory]);

  useEffect(() => {
    const interval = setInterval(fetchHistory, 15000);
    return () => clearInterval(interval);
  }, [fetchHistory]);

  const successCount = rows.filter((r) => r.success).length;
  const failureCount = rows.filter((r) => !r.success).length;

  const columns = [
    {
      title: "Template",
      dataIndex: "templateName",
      width: 150,
      render: (v) => <span style={{ fontFamily: "monospace", fontSize: 12.5 }}>{v}</span>,
    },
    {
      title: "Recipient",
      dataIndex: "toEmail",
      render: (v, r) => (
        <div>
          <div style={{ fontSize: 13 }}>{v}</div>
          {r.toName && <div style={{ fontSize: 11, color: "#9ca3af" }}>{r.toName}</div>}
        </div>
      ),
    },
    {
      title: "Status",
      dataIndex: "success",
      width: 100,
      render: (v) =>
        v ? (
          <Tag color="#43A047" style={{ borderRadius: 12 }}>Sent</Tag>
        ) : (
          <Tag color="#E24B4A" style={{ borderRadius: 12 }}>Failed</Tag>
        ),
    },
    {
      title: "Why it failed",
      dataIndex: "errorMessage",
      render: (v) =>
        v ? (
          <span style={{ fontSize: 12, color: "#991b1b", wordBreak: "break-word" }}>{v}</span>
        ) : (
          <span style={{ color: "#d1d5db" }}>—</span>
        ),
    },
    {
      title: "Sent At",
      dataIndex: "sentAt",
      width: 170,
      render: (v) => (v ? new Date(v).toLocaleString() : ""),
      sorter: (a, b) => new Date(a.sentAt) - new Date(b.sentAt),
      defaultSortOrder: "descend",
    },
  ];

  return (
    <div>
      <div className="page-header">
        <div>
          <div className="page-title">
            <MailOutlined style={{ marginRight: 8, color: "#4a90d9" }} />
            Transactional Email Log
          </div>
          <div className="page-sub">
            Order confirmations, OTPs, password resets, and account emails — every send attempt
            {lastUpdated && ` · last updated ${lastUpdated.toLocaleTimeString()}`}
          </div>
        </div>
        <Button icon={<ReloadOutlined />} onClick={fetchHistory} loading={loading}>
          Refresh
        </Button>
      </div>

      <div style={{ display: "flex", gap: 12, marginBottom: 20, flexWrap: "wrap" }}>
        <Select
          allowClear
          placeholder="All templates"
          value={template}
          onChange={setTemplate}
          style={{ width: 200 }}
          options={TEMPLATES.map((t) => ({ label: t, value: t }))}
        />
        <Select
          allowClear
          placeholder="All statuses"
          value={success}
          onChange={setSuccess}
          style={{ width: 160 }}
          options={[
            { label: "Sent", value: true },
            { label: "Failed", value: false },
          ]}
        />
      </div>

      <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(160px, 1fr))", gap: 16, marginBottom: 24 }}>
        <StatCard label="Shown (last 200 max)" value={rows.length} />
        <StatCard label="Sent" value={successCount} color="#1d9e75" />
        <StatCard label="Failed" value={failureCount} color={failureCount > 0 ? "#e24b4a" : "#111"} />
      </div>

      <Table
        rowKey="id"
        columns={columns}
        dataSource={rows}
        loading={loading && rows.length === 0}
        pagination={{ pageSize: 20 }}
        size="middle"
      />
    </div>
  );
}
